<template>
  <div
    v-intersection="handleIntersection"
    class="c-chart-library-chart"
    data-testid="chart-library-chart"
    :style="chartStyle"
  >
    <BaseDataLoadingWrapper
      class="column no-wrap"
      :state="state"
      @reload="emit('reload')"
    >
      <slot name="before" />

      <template
        v-if="$slots.loading"
        #loading
      >
        <slot name="loading" />
      </template>

      <Transition name="fade">
        <Component
          :is="chartComponent"
          v-if="isIntersecting"
          v-bind="chartProps"
          class="fit self-center"
          :interaction-enabled="Boolean(chartConfig.interactionEnabled)"
          @chart-clicked="emit('chart-clicked', $event)"
          @chart:render="emit('chart:render', $event)"
        />
      </Transition>
    </BaseDataLoadingWrapper>
  </div>
</template>

<script setup lang="ts">
import type { Chart } from "chart.js";
import { merge } from "lodash-es";
import type { IntersectionValue } from "quasar";
import { type Component, computed, type CSSProperties, ref } from "vue";

import { appInstance } from "shared/boot/app";
import { BaseDataLoadingWrapper } from "shared/components/base";
import type { ChartConfig } from "shared/modules/Charts/types/chartConfig";
import type { ChartClickEvent } from "shared/modules/Charts/types/chartInteractions";
import type { ChartLibraryProps } from "shared/modules/Charts/types/data";
import { DataLoadingWrapperState } from "shared/types";

const props = defineProps<{
  chartConfig: ChartConfig;
  chartData: ChartLibraryProps;
  error?: boolean;
  intersectOnce?: boolean;
  loading?: boolean;
}>();

const emit = defineEmits<{
  "chart-clicked": [ChartClickEvent];
  "chart:render": [Chart];
  intersection: [boolean];
  reload: [];
}>();

const isIntersecting = ref(false);

const state = computed<DataLoadingWrapperState>(() => {
  if (props.loading) return DataLoadingWrapperState.Loading;
  if (props.error) return DataLoadingWrapperState.Error;

  return DataLoadingWrapperState.Done;
});

const chartComponent = computed<Component | undefined>(() =>
  appInstance.component(props.chartConfig.component)
);

const chartStyle = computed<CSSProperties | undefined>(
  () => props.chartConfig.style
);

const chartProps = computed<ChartLibraryProps>(() =>
  merge({}, props.chartData, props.chartConfig.componentAttributes)
);

const handleIntersection: IntersectionValue = (el) => {
  isIntersecting.value = el.isIntersecting;
  emit("intersection", isIntersecting.value);

  if (isIntersecting.value && props.intersectOnce) {
    return false;
  }

  return true;
};
</script>

<style lang="scss" scoped>
.c-chart-library-chart {
  width: 100%;
}
</style>
